// src/App.js
// Muster: CRM App.js - AuthProvider, PrivateRoute/AdminRoute, Login als eigene Route.
//
// Wichtig: Die Tab-Inhalte werden NICHT mehr über <Routes>/<Route> ein- und ausgehängt (das
// würde die jeweilige Komponente bei jedem Tab-Wechsel unmounten und ihren lokalen State
// verlieren, z.B. die erfasste Rechnungsliste in Extraction). Stattdessen bleiben alle Tabs
// durchgehend gemountet, nur die Sichtbarkeit wird per CSS umgeschaltet (TabPanel). Die URL
// bleibt weiterhin die Quelle der Wahrheit dafür, welcher Tab gerade aktiv ist.
//
// Dokumentklassen-Tabs (Extraction, künftig Lieferschein etc.) kommen jetzt aus der Registry
// (dokumentklassen/index.js) statt einzeln hart codiert zu sein - eine neue Dokumentklasse
// hinzuzufügen bedeutet damit: nur die Registry ergänzen, App.js bleibt unangetastet.
// Users bleibt bewusst separat verdrahtet (kein Dokumenttyp, sondern Admin-Verwaltung).
//
// DialogProvider (Ersatz für window.confirm/window.alert, siehe components/shared/Dialog.jsx)
// wrappt die ganze App, damit useDialog() überall verfügbar ist - auch auf der Login-Seite.
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import './App.css';
import Header from './components/Header';
import MainContent from './components/MainContent';
import Users from './tabs/Users';
import System from './tabs/System';
import LoginForm from './LoginForm';
import { AuthProvider, useAuth } from './authContext';
import { DialogProvider } from './components/shared/Dialog';
import { useMobilAnsichtErzwingen } from './components/shared/useMobilAnsichtErzwingen';
import { DOKUMENTKLASSEN } from './dokumentklassen';

// PrivateRoute-Komponente
const PrivateRoute = ({ children }) => {
  const { isAuthenticated } = useAuth();
  return isAuthenticated ? children : <Navigate to="/login" replace />;
};

function TabPanel({ aktiv, children }) {
  return <div style={{ display: aktiv ? 'block' : 'none' }}>{children}</div>;
}

// Haupt-App-Komponente
const MainApp = () => {
  const { logout, token, role, hatRolle } = useAuth();
  const location = useLocation();
  const [mobilAnsichtErzwingen] = useMobilAnsichtErzwingen();

  const handleLogout = () => {
    logout();
  };

  const aktuellerTab = location.pathname === '/' ? '/extraction' : location.pathname;
  const berechtigungenGeladen = !(token && role === null);
  const istAdmin = berechtigungenGeladen && hatRolle('Admin');
  const istSystem = berechtigungenGeladen && hatRolle('System');

  return (
    <div className={`app-container${mobilAnsichtErzwingen ? ' mobil-ansicht-erzwungen' : ''}`}>
      <Header onLogout={handleLogout} />
      <MainContent>
        {DOKUMENTKLASSEN.map(({ pfad, component: Komponente }) => (
          <TabPanel key={pfad} aktiv={aktuellerTab === pfad}>
            <Komponente />
          </TabPanel>
        ))}
        {istAdmin && (
          <TabPanel aktiv={aktuellerTab === '/users'}>
            <Users />
          </TabPanel>
        )}
        {istSystem && (
          <TabPanel aktiv={aktuellerTab === '/system'}>
            <System />
          </TabPanel>
        )}
        {aktuellerTab === '/users' && !berechtigungenGeladen && <div>Lade Berechtigungen...</div>}
        {aktuellerTab === '/system' && !berechtigungenGeladen && <div>Lade Berechtigungen...</div>}
        <Routes>
          <Route path="/" element={<Navigate to="/extraction" replace />} />
        </Routes>
      </MainContent>
    </div>
  );
};

// Haupt-App mit Routing
const App = () => {
  return (
    <AuthProvider>
      <DialogProvider>
        <Router>
          <Routes>
            <Route path="/login" element={<LoginForm />} />
            <Route
              path="/*"
              element={
                <PrivateRoute>
                  <MainApp />
                </PrivateRoute>
              }
            />
          </Routes>
        </Router>
      </DialogProvider>
    </AuthProvider>
  );
};

export default App;